import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './ClassDetails.css';

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
};

const ClassDetails = ({ yogaClass, onClose }) => {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [parsedImages, setParsedImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [parsedBenefits, setParsedBenefits] = useState([]);
  const [imageErrors, setImageErrors] = useState({});
  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/class/${yogaClass.id}`;
    await copyToClipboard(url);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2500);
  };
  
  // Solution 1: Prevent body scroll when modal is open
  useEffect(() => {
    // Save current body overflow state
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    
    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    // Disable body scroll
    document.body.style.overflow = 'hidden';
    
    // Add padding to compensate for scrollbar width (prevents content jump)
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    
    // Cleanup: restore original state when modal closes
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, []); // Empty dependency array - run once on mount, cleanup on unmount
  
  // Parse benefits if they exist
  useEffect(() => {
    if (yogaClass?.benefits) {
      try {
        // Check if benefits is already an array
        if (Array.isArray(yogaClass.benefits)) {
          setParsedBenefits(yogaClass.benefits);
        } else {
          // Try to parse as JSON if it's a string
          const benefitsArray = JSON.parse(yogaClass.benefits);
          if (Array.isArray(benefitsArray)) {
            setParsedBenefits(benefitsArray);
          } else {
            // If not an array, create a single-item array
            setParsedBenefits([yogaClass.benefits]);
          }
        }
      } catch (e) {
        // If parsing fails, treat as plain text
        setParsedBenefits([yogaClass.benefits]);
      }
    } else {
      setParsedBenefits([]);
    }
  }, [yogaClass]);
  
  // Fetch or parse images for the class
  useEffect(() => {
    if (!yogaClass || !yogaClass.id) return;
    
    // console.log('Complete yogaClass object:', yogaClass);
    
    // If we already have images from the parent component, use those
    if (yogaClass.images && Array.isArray(yogaClass.images) && yogaClass.images.length > 0) {
      // console.log('Using images passed from parent:', yogaClass.images);
      setParsedImages(yogaClass.images);
      setCurrentImageIndex(0);
      return;
    }
    
    setIsLoading(true);
    axios.get(`classes/${yogaClass.id}/images`)
      .then(response => {
        // console.log('Fetched images from API:', response.data);
        
        if (response.data.images && Array.isArray(response.data.images)) {
          setParsedImages(response.data.images);
          setCurrentImageIndex(0); // Reset to first image when class changes
        } else {
          setParsedImages([]);
        }
      })
      .catch(error => {
        // console.error('Error fetching class images:', error);
        setParsedImages([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [yogaClass]);
  
  // Format time (e.g., "14:00" -> "14:00")
  const formatTime = (timeStr) => {
    try {
      if (!timeStr) return 'N/A';
      
      const [hours, minutes] = timeStr.split(':').map(Number);
      
      // Check if we have valid numbers
      if (isNaN(hours) || isNaN(minutes)) return timeStr;
      
      // Format in 24-hour with leading zeros
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      // console.error('Error formatting time:', error);
      return timeStr || 'N/A';
    }
  };

  // Format date properly
  const formatDate = (dateString) => {
    try {
      if (!dateString) return '';
      
      // Check if it's already in a specific format or if it's an ISO string
      if (dateString.includes('T')) {
        // Handle ISO date strings
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        // Format: DD.MM.YYYY
        return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
      } else {
        // Handle other date formats (like MM/DD/YYYY)
        const parts = dateString.split(/[\/\-\.]/);
        if (parts.length !== 3) return dateString;
        
        // Try to determine format based on values
        if (parts[0].length === 4) {
          // YYYY-MM-DD
          return `${parts[2].padStart(2, '0')}.${parts[1].padStart(2, '0')}.${parts[0]}`;
        } else if (parseInt(parts[0]) > 12) {
          // DD/MM/YYYY
          return `${parts[0].padStart(2, '0')}.${parts[1].padStart(2, '0')}.${parts[2]}`;
        } else {
          // MM/DD/YYYY (assume US format)
          return `${parts[1].padStart(2, '0')}.${parts[0].padStart(2, '0')}.${parts[2]}`;
        }
      }
    } catch (error) {
      // console.error('Error formatting date:', error);
      return dateString;
    }
  };
  
  // Format full date with time
  const formatDateTime = (dateStr, timeStr) => {
    try {
      if (!dateStr) return '';
      
      const formattedDate = formatDate(dateStr);
      const formattedTime = formatTime(timeStr);
      
      return `${formattedTime} - ${formattedDate}`;
    } catch (error) {
      // console.error('Error formatting date time:', error);
      return '';
    }
  };

  // Get difficulty text based on numerical value
  const getDifficultyText = (difficulty) => {
    if (!difficulty) return t('normal');
    
    // Convert to number if it's a string
    const difficultyNum = parseInt(difficulty, 10);
    
    switch(difficultyNum) {
      case 1:
        return t('oddych');
      case 2:
        return t('normal');
      case 3:
        return t('makacka');
      default:
        return t('normal');
    }
  };
  
  // Image navigation functions with explicit event handling
  const handleNextImage = () => {
    if (parsedImages.length <= 1) return;
    
    const newIndex = currentImageIndex === parsedImages.length - 1 ? 0 : currentImageIndex + 1;
    // console.log('Next image clicked, changing from', currentImageIndex, 'to', newIndex);
    setCurrentImageIndex(newIndex);
  };
  
  const handlePrevImage = () => {
    if (parsedImages.length <= 1) return;
    
    const newIndex = currentImageIndex === 0 ? parsedImages.length - 1 : currentImageIndex - 1;
    // console.log('Previous image clicked, changing from', currentImageIndex, 'to', newIndex);
    setCurrentImageIndex(newIndex);
  };
  
  const handleIndicatorClick = (index) => {
    // console.log('Indicator clicked, changing from', currentImageIndex, 'to', index);
    setCurrentImageIndex(index);
  };

  // Handle image error with more reliable fallbacks
  const handleImageError = (index) => {
    // console.error('Image failed to load:', parsedImages[index]);
    setImageErrors(prev => ({...prev, [index]: true}));
  };

  // Get image source with fallback mechanism
  const getImageSource = (index) => {
    if (imageErrors[index]) {
      // Return null to indicate image should not be shown
      return null;
    }
    return parsedImages[index];
  };

  // Gallery component with redesigned navigation
  const ImageGallery = () => {
    if (isLoading) {
      return <div className="loading-spinner">{t('loading_images')}</div>;
    }
    
    if (!parsedImages.length) {
      return <div className="no-images-message"><p>Žiadne obrázky pre túto hodinu</p></div>;
    }
    
    // If all images have errors, show the no images message
    const allImagesHaveErrors = parsedImages.every((_, index) => imageErrors[index]);
    if (allImagesHaveErrors) {
      return <div className="no-images-message"><p>Žiadne obrázky pre túto hodinu</p></div>;
    }
    
    return (
      <div className="class-image-gallery">
        {/* Current image */}
        <div className="gallery-image-container">
          {getImageSource(currentImageIndex) ? (
            <img 
              src={getImageSource(currentImageIndex)} 
              alt={`${yogaClass.title || t('yoga_class')} - Image ${currentImageIndex + 1}`} 
              className="class-image"
              onError={() => handleImageError(currentImageIndex)}
              style={{ objectPosition: 'center center' }}
            />
          ) : (
            <div className="no-images-message"><p>Žiadne obrázky pre túto hodinu</p></div>
          )}
        </div>
        
        {/* Navigation controls - only show if multiple images and not all have errors */}
        {parsedImages.length > 1 && !allImagesHaveErrors && (
          <>
            {/* Previous button */}
            <button 
              type="button"
              className="gallery-nav-button prev-button"
              onClick={handlePrevImage}
              aria-label="Previous image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            
            {/* Next button */}
            <button 
              type="button"
              className="gallery-nav-button next-button"
              onClick={handleNextImage}
              aria-label="Next image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
            
            {/* Indicators for direct navigation */}
            <div className="gallery-indicators">
              {parsedImages.map((_, index) => (
                <button
                  key={index}
                  type="button" 
                  className={`indicator-button ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => handleIndicatorClick(index)}
                  aria-label={`Go to image ${index + 1}`}
                  aria-current={index === currentImageIndex ? 'true' : 'false'}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // Create modal content
  const detailsModalContent = (
    <div className="class-details-overlay" onClick={onClose}>
      <div className="class-details-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close-button" onClick={onClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        {/* Gallery section */}
        <ImageGallery />
        
        {/* Title section (moved under gallery) */}
        <div className="class-title-container">
          <h2>{yogaClass.title || yogaClass.class_name || t('yoga_class')}</h2>
          <span>---</span>
        </div>
        
        <div className="class-instructor">
          <div className="instructor-image">
            <img 
              src={yogaClass.instructor_image} 
              alt={yogaClass.instructor || t('staff')} 
              onError={(e) => {
                // console.error('Instructor image failed to load');
                e.target.parentNode.innerHTML = '<div class="no-image-text">Žiadne obrázky pre túto hodinu</div>';
              }}
            />
          </div>
          <div className="instructor-info">
            <h3>{t('instructor')}</h3>
            <p>{yogaClass.instructor || t('staff')}</p>
          </div>
        </div>
        
        <div className="content-grid">
          {/* Class description - moved to top */}
          <div className="class-description">
            <h3 className="section-title">{t('description')}</h3>
            <p className="formatted-description" style={{ whiteSpace: 'pre-wrap' }}>
              {yogaClass.description || t('no_description_available')}
            </p>
          </div>
          
          {/* Benefits section - new */}
          {parsedBenefits.length > 0 && (
            <div className="class-benefits">
              <h3 className="section-title">{t('benefits')}</h3>
              <div className="benefits-container">
                {parsedBenefits.map((benefit, index) => (
                  <div key={index} className="benefit-tag">
                    <span className="benefit-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </span>
                    <span className="benefit-text">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Details section - moved to bottom */}
          <div className="details-section">
            <h3 className="section-title">{t('class_details')}</h3>
            
            <div className="details-list">
              <div className="detail-item">
                <span className="detail-label">{t('time')}</span>
                <span className="detail-value time-value">
                  {yogaClass.date_time ? 
                    formatDateTime(yogaClass.date, yogaClass.start_time) : 
                    `${formatTime(yogaClass.start_time)} - ${formatTime(yogaClass.end_time)}`
                  }
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">{t('location')}</span>
                <span className="detail-value">{yogaClass.location || 'Main Studio'}</span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">{t('difficulty')}</span>
                <span className="detail-value">{getDifficultyText(yogaClass.difficulty)}</span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">{t('available_spots')}</span>
                <span className="detail-value">{yogaClass.available_spots || 0} {t('of')} {yogaClass.max_capacity || 10}</span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">{t('price')}</span>
                <span className="detail-value">{yogaClass.price !== undefined ? yogaClass.price : 0} 
                  <span className="currency-symbol">EUR</span>
                </span>
              </div>
            </div>
          </div>
          
          {/* Payment Methods section */}
          <div className="payment-methods-section">
            <h3 className="section-title">{t('payment_methods') || 'Payment Methods'}</h3>
            <div className="payment-methods-container">
              <div className="payment-method-item">
                <div className="payment-method-icon">
                  <img 
                    src={require('./payment_methods/multisport.jpg')} 
                    alt="MultiSport" 
                    className="payment-logo"
                    onError={(e) => {
                      e.target.parentNode.innerHTML = '<div class="no-image-text">Žiadne obrázky</div>';
                    }}
                  />
                </div>
                <span className="payment-method-name">MultiSport</span>
              </div>
              
              <div className="payment-method-item">
                <div className="payment-method-icon">
                  <img 
                    src={require('./payment_methods/upbalance.jpg')} 
                    alt="UpBalansea" 
                    className="payment-logo"
                    onError={(e) => {
                      e.target.parentNode.innerHTML = '<div class="no-image-text">Žiadne obrázky</div>';
                    }}
                  />
                </div>
                <span className="payment-method-name">UpBalansea</span>
              </div>
              
              <div className="payment-method-item">
                <div className="payment-method-icon cash-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z"/>
                  </svg>
                </div>
                <span className="payment-method-name">Cash</span>
              </div>
              
              <div className="payment-method-item">
                <div className="payment-method-icon gift-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/>
                  </svg>
                </div>
                <span className="payment-method-name">Gift Card</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="class-actions">
          <button
            type="button"
            className="action-button modal-share-action"
            onClick={handleShare}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            {shareCopied ? (t('link_copied') || 'Copied!') : (t('share') || 'Share')}
          </button>
          <button 
            type="button"
            className="action-button" 
            onClick={onClose}
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render the modal outside of the normal DOM hierarchy
  return ReactDOM.createPortal(
    detailsModalContent,
    document.getElementById('popup-root')
  );
};

export default ClassDetails; 